import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, MapPin, Calendar, CalendarDays, FileText, Users, ShoppingCart, Shield, Mail, Plus, ExternalLink, X, Loader2, Check, AlertCircle, UserCheck, Upload, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import BeneficialOwnersSection from '@/components/app/BeneficialOwnersSection';
import ClientDataRoomSection from '@/components/app/ClientDataRoomSection';
import MemberPassportCell from '@/components/app/MemberPassportCell';
import { ID_DOC_TYPES, CONTROL_BASIS_OPTIONS } from '@/components/app/BeneficialOwnersSection';
import { extractErrorMessage } from '@/utils/errorHelpers';
import { formatLongDate } from '@/utils/dateFormat';
import { raLabel } from '@/lib/foreignCatalog';

const FOREIGN_OWNER_ROLES = ['Beneficial Owner', 'Shareholder', 'Director', 'Member', 'Partner', 'Nominee Director'];

// Modal to add a member / director / beneficial owner to an international company.
const AddForeignOwnerModal = ({ companyId, token, api, preselectBO, onClose, onAdded }) => {
  const [ownerType, setOwnerType] = React.useState('individual');
  const [fullName, setFullName] = React.useState('');
  const [pct, setPct] = React.useState('');
  const [roles, setRoles] = React.useState(preselectBO ? ['Beneficial Owner'] : []);
  const [nationality, setNationality] = React.useState('');
  const [idNumber, setIdNumber] = React.useState('');
  const [doc, setDoc] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const isCompany = ownerType === 'company';
  const inputCls = 'w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#059669]';

  const toggleRole = (r) => setRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr('');
    try {
      const pres = await axios.post(`${api}/upload/presigned`, { file_name: file.name, content_type: file.type || 'application/octet-stream' }, { headers: { Authorization: `Bearer ${token}` } });
      const { upload_url, file_url, s3_key } = pres.data;
      await axios.put(upload_url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      setDoc({ file_name: file.name, file_url, s3_key });
    } catch (e) { setErr('Upload failed. Please try again.'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!fullName.trim() || !pct || !nationality.trim() || !idNumber.trim()) { setErr('All fields are mandatory.'); return; }
    if (roles.length === 0) { setErr('Select at least one role.'); return; }
    if (!doc) { setErr(`Upload the ${isCompany ? 'formation certificate' : 'passport'}.`); return; }
    setSaving(true); setErr('');
    try {
      await axios.post(`${api}/companies/${companyId}/owners`, {
        owner_type: ownerType,
        full_name: fullName.trim(),
        ownership_percentage: parseFloat(pct),
        role: roles[0],
        roles,
        id_type: isCompany ? 'company_registration' : 'passport',
        id_number: idNumber.trim(),
        nationality: nationality.trim(),
        document_type: isCompany ? 'formation_certificate' : 'passport',
        document_file_name: doc.file_name,
        document_url: doc.file_url,
        document_s3_key: doc.s3_key,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onAdded();
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to add. Please try again.'); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="add-foreign-owner-modal">
      <div className="bg-[#111827] border border-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Add Member / Beneficial Owner</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="afo-close"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">{err}</div>}
        <div className="flex gap-2 mb-4">
          {['individual', 'company'].map(t => (
            <button key={t} onClick={() => setOwnerType(t)} className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-all ${ownerType === t ? 'border-[#059669] bg-[#059669]/10 text-[#059669]' : 'border-gray-700 text-gray-300'}`} data-testid={`afo-type-${t}`}>
              {t === 'individual' ? 'Individual' : 'Owned by a Company'}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder={isCompany ? 'Company name *' : 'Full name *'} className={inputCls} data-testid="afo-name" />
          <input type="number" value={pct} onChange={e => setPct(e.target.value)} placeholder="Ownership % *" className={inputCls} data-testid="afo-pct" />
          <input value={nationality} onChange={e => setNationality(e.target.value)} placeholder={isCompany ? 'Country of incorporation *' : 'Nationality *'} className={inputCls} data-testid="afo-nationality" />
          <input value={idNumber} onChange={e => setIdNumber(e.target.value)} placeholder={isCompany ? 'Registration no. *' : 'Passport number *'} className={inputCls} data-testid="afo-idnum" />
        </div>
        <div className="mt-3">
          <label className="block text-sm text-gray-400 mb-2">Role(s) <span className="text-red-400">*</span></label>
          <div className="flex flex-wrap gap-2">
            {FOREIGN_OWNER_ROLES.map(r => (
              <button key={r} type="button" onClick={() => toggleRole(r)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${roles.includes(r) ? 'border-[#059669] bg-[#059669]/15 text-[#059669]' : 'border-gray-700 text-gray-300'}`} data-testid={`afo-role-${r.replace(/\s+/g, '-').toLowerCase()}`}>
                {roles.includes(r) && <Check className="w-3 h-3 inline mr-1" />}{r}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-sm text-gray-400 mb-2">{isCompany ? 'Formation Certificate' : 'Passport'} <span className="text-red-400">*</span></label>
          {doc ? (
            <div className="flex items-center justify-between bg-[#059669]/10 border border-[#059669]/30 rounded-lg px-3 py-2">
              <span className="text-[#059669] text-sm truncate flex items-center gap-2"><Check className="w-4 h-4" /> {doc.file_name}</span>
              <button onClick={() => setDoc(null)} className="text-gray-400 hover:text-red-400 text-xs">Replace</button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-700 hover:border-[#059669] rounded-lg py-3 cursor-pointer text-gray-300" data-testid="afo-upload-label">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload document</>}
              <input type="file" accept="image/*,application/pdf" className="hidden" disabled={uploading} onChange={e => upload(e.target.files[0])} data-testid="afo-upload" />
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-gray-300 bg-gray-800 hover:bg-gray-700 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg text-white font-medium disabled:opacity-50 text-sm" style={{ backgroundColor: '#059669' }} data-testid="afo-save">
            {saving ? 'Saving...' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Entity type display mapping
const ENTITY_TYPES = {
  'us_llc': 'US LLC',
  'us_c_corp': 'US C-Corp',
  'us_s_corp': 'US S-Corp',
  'us_sole_proprietorship': 'US Sole Proprietorship',
};

// Member title options based on entity type.
// LLC and C-Corp are the only entity types we actively form. Other entity
// types (S-Corp, Sole Proprietorship) that exist as legacy records fall
// through to the LLC list via the lookup default below.
// Entity-aware titles — kept in sync with backend US_TITLES_BY_ENTITY in server.py
// and admin SuperAdminCompanyDetail.jsx.
const MEMBER_TITLES = {
  'us_llc':                  ['Member', 'Manager', 'Member / Manager'],
  'us_c_corp':               ['Director', 'Officer', 'Director & Officer', 'Shareholder'],
  'us_s_corp':               ['Director', 'Officer', 'Director & Officer', 'Shareholder'],
  'us_sole_proprietorship':  ['Owner', 'Sole Proprietor'],
};

// Client-side read-only KYC badge with hint + rejection reason
const ClientKYCBadge = ({ member }) => {
  const status = member?.kyc_status || 'pending';
  if (status === 'verified') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-[#059669]/15 text-[#059669]"
        data-testid={`member-kyc-badge-${member.id}`}
      >
        <Shield className="w-3 h-3" />
        Verified
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex flex-col gap-1 max-w-xs" data-testid={`member-kyc-badge-${member.id}`}>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-500/15 text-red-400 self-start">
          <Shield className="w-3 h-3" />
          Rejected
        </span>
        {member.kyc_rejection_reason && (
          <span className="text-xs text-red-300/80" data-testid={`member-kyc-reason-${member.id}`}>
            <span className="font-medium">Reason:</span> {member.kyc_rejection_reason}
          </span>
        )}
        <span className="text-xs text-amber-400/80">
          Please upload a corrected passport document
        </span>
      </div>
    );
  }
  // pending (default)
  return (
    <div className="flex flex-col gap-0.5" data-testid={`member-kyc-badge-${member.id}`}>
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 self-start">
        <Shield className="w-3 h-3" />
        Pending
      </span>
      <span className="text-xs text-gray-500">Identity verification in progress</span>
    </div>
  );
};

// Order status display
const STATUS_STYLES = {
  'draft': { bg: 'bg-gray-700', text: 'text-gray-300' },
  'pending': { bg: 'bg-gray-600/20', text: 'text-gray-400' },
  'pending_review': { bg: 'bg-amber-500/20', text: 'text-amber-400' },
  'pending_payment': { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  'payment_pending': { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  'paid': { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  'in_progress': { bg: 'bg-purple-500/20', text: 'text-purple-400' },
  'submitted': { bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
  'approved': { bg: 'bg-[#059669]/20', text: 'text-[#059669]' },
  'completed': { bg: 'bg-emerald-600/20', text: 'text-emerald-400' },
  'rejected': { bg: 'bg-red-500/20', text: 'text-red-400' },
};

// Add Member Inline Card (matches BeneficialOwnersSection BO form style)
const AddMemberCard = ({ onCancel, onSuccess, companyId, entityType, token }) => {
  const [formData, setFormData] = useState({
    full_legal_name: '',
    email: '',
    phone: '',
    country_of_residence: '',
    ownership_percentage: '',
    us_title: '',
    is_us_resident: false,
    date_of_birth: '',
    // BO fields
    is_beneficial_owner: false,
    bo_residential_address: { street: '', city: '', state: '', postal: '', country: 'US' },
    bo_id_document_type: '',
    bo_id_document_number_last4: '',
    bo_id_document_country: 'US',
    bo_has_substantial_control: false,
    bo_control_basis: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const titles = MEMBER_TITLES[entityType] || MEMBER_TITLES['us_llc'];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBOAddrChange = (field, value) => {
    setFormData(prev => ({
      ...prev, bo_residential_address: { ...prev.bo_residential_address, [field]: value }
    }));
  };

  // Auto-populate BO fields when checkbox is ticked
  const handleBOToggle = (checked) => {
    setFormData(prev => {
      const updated = { ...prev, is_beneficial_owner: checked };
      if (checked) {
        // Auto-populate from member fields
        if (!updated.date_of_birth) updated.date_of_birth = prev.date_of_birth || '';
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.full_legal_name.trim()) { setError('Full Legal Name is required'); return; }
    if (!formData.email.trim()) { setError('Email is required'); return; }
    if (!formData.country_of_residence.trim()) { setError('Country of Residence is required'); return; }

    if (formData.is_beneficial_owner) {
      if (!formData.date_of_birth) { setError('Date of birth is required for beneficial owners'); return; }
      const addr = formData.bo_residential_address;
      if (!addr.street || !addr.city || !addr.state || !addr.postal) { setError('Complete residential address is required for beneficial owners'); return; }
      if (!formData.bo_id_document_type) { setError('ID document type is required for beneficial owners'); return; }
      if (!formData.bo_id_document_number_last4) { setError('Last 4 of document number is required'); return; }
      const pct = formData.ownership_percentage ? parseFloat(formData.ownership_percentage) : null;
      if (!formData.bo_has_substantial_control && (!pct || pct <= 0)) {
        setError('Beneficial owner must own >0% or exercise substantial control'); return;
      }
      if (formData.bo_has_substantial_control && !formData.bo_control_basis) {
        setError('Control basis is required when substantial control is checked'); return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        full_legal_name: formData.full_legal_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        country_of_residence: formData.country_of_residence.trim(),
        ownership_percentage: formData.ownership_percentage ? parseFloat(formData.ownership_percentage) : null,
        us_title: formData.us_title || null,
        is_us_resident: formData.is_us_resident,
        date_of_birth: formData.date_of_birth || null,
        is_beneficial_owner: formData.is_beneficial_owner,
      };
      if (formData.is_beneficial_owner) {
        payload.bo_residential_address = formData.bo_residential_address;
        payload.bo_id_document_type = formData.bo_id_document_type;
        payload.bo_id_document_number_last4 = formData.bo_id_document_number_last4;
        payload.bo_id_document_country = formData.bo_id_document_country;
        payload.bo_has_substantial_control = formData.bo_has_substantial_control;
        payload.bo_control_basis = formData.bo_has_substantial_control ? formData.bo_control_basis : null;
      }

      await axios.post(`${API}/companies/${companyId}/members`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onSuccess();
      onCancel();
      setFormData({
        full_legal_name: '', email: '', phone: '', country_of_residence: '',
        ownership_percentage: '', us_title: '', is_us_resident: false, date_of_birth: '',
        is_beneficial_owner: false,
        bo_residential_address: { street: '', city: '', state: '', postal: '', country: 'US' },
        bo_id_document_type: '', bo_id_document_number_last4: '', bo_id_document_country: 'US',
        bo_has_substantial_control: false, bo_control_basis: '',
      });
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to add member'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#059669]";
  const inputSmCls = "w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#059669]";

  return (
    <div className="mb-4 p-4 bg-[#0b1310] border border-gray-700 rounded-lg" data-testid="add-member-card">
      <h3 className="text-sm font-medium text-white mb-3">Add Member</h3>
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Full Legal Name *</label>
              <input type="text" value={formData.full_legal_name}
                onChange={(e) => handleChange('full_legal_name', e.target.value)}
                className={inputCls} placeholder="John Smith" data-testid="member-name-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Email *</label>
              <input type="email" value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={inputCls} placeholder="john@example.com" data-testid="member-email-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Phone</label>
              <input type="tel" value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className={inputCls} placeholder="+1 (555) 123-4567" />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Date of Birth</label>
              <input type="date" value={formData.date_of_birth}
                onChange={(e) => handleChange('date_of_birth', e.target.value)}
                className={inputCls} data-testid="member-dob-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Country of Residence *</label>
              <input type="text" value={formData.country_of_residence}
                onChange={(e) => handleChange('country_of_residence', e.target.value)}
                className={inputCls} placeholder="United States" data-testid="member-country-input" />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Ownership %</label>
              <input type="number" min="0" max="100" step="0.01"
                value={formData.ownership_percentage}
                onChange={(e) => handleChange('ownership_percentage', e.target.value)}
                className={inputCls} placeholder="25" />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Title</label>
              <select value={formData.us_title}
                onChange={(e) => handleChange('us_title', e.target.value)}
                className={inputCls} data-testid="member-title-select">
                <option value="">Select title</option>
                {titles.map((title) => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Is US Resident?</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => handleChange('is_us_resident', true)}
                  className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                    formData.is_us_resident ? 'bg-[#059669] text-black' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>
                  Yes
                </button>
                <button type="button" onClick={() => handleChange('is_us_resident', false)}
                  className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                    !formData.is_us_resident ? 'bg-[#059669] text-black' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>
                  No
                </button>
              </div>
            </div>

            {/* BO Checkbox */}
            <div className="border-t border-gray-800 pt-4">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="bo-checkbox-label">
                <input type="checkbox" checked={formData.is_beneficial_owner}
                  onChange={(e) => handleBOToggle(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 text-[#059669] focus:ring-[#059669] bg-gray-700"
                  data-testid="member-bo-checkbox" />
                <div>
                  <span className="text-white text-sm font-medium">This member is also a Beneficial Owner</span>
                  <p className="text-gray-500 text-xs mt-0.5">Owns 25%+ or exercises substantial control</p>
                </div>
              </label>
            </div>

            {/* BO Expanded Fields */}
            {formData.is_beneficial_owner && (
              <div className="space-y-3 p-4 bg-gray-800/30 border border-gray-700/50 rounded-lg" data-testid="bo-expanded-fields">
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Beneficial Owner Details</p>

                {/* Substantial Control */}
                <div className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.bo_has_substantial_control}
                      onChange={e => handleChange('bo_has_substantial_control', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-600 text-[#059669] focus:ring-[#059669] bg-gray-700"
                      data-testid="member-bo-control-check" />
                    <span className="text-sm text-gray-300">Exercises substantial control</span>
                  </label>
                  {formData.bo_has_substantial_control && (
                    <select value={formData.bo_control_basis}
                      onChange={e => handleChange('bo_control_basis', e.target.value)}
                      className={inputSmCls} data-testid="member-bo-control-basis">
                      <option value="">Select control basis...</option>
                      {CONTROL_BASIS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Residential Address *</label>
                  <div className="space-y-2">
                    <input type="text" value={formData.bo_residential_address.street}
                      onChange={e => handleBOAddrChange('street', e.target.value)}
                      className={inputSmCls} placeholder="Street" data-testid="member-bo-street" />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <input type="text" value={formData.bo_residential_address.city}
                        onChange={e => handleBOAddrChange('city', e.target.value)} className={inputSmCls} placeholder="City" />
                      <input type="text" value={formData.bo_residential_address.state}
                        onChange={e => handleBOAddrChange('state', e.target.value)} className={inputSmCls} placeholder="State" />
                      <input type="text" value={formData.bo_residential_address.postal}
                        onChange={e => handleBOAddrChange('postal', e.target.value)} className={inputSmCls} placeholder="ZIP" />
                      <input type="text" value={formData.bo_residential_address.country}
                        onChange={e => handleBOAddrChange('country', e.target.value)} className={inputSmCls} placeholder="US" />
                    </div>
                  </div>
                </div>

                {/* ID Document */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">ID Doc Type *</label>
                    <select value={formData.bo_id_document_type}
                      onChange={e => handleChange('bo_id_document_type', e.target.value)}
                      className={inputSmCls} data-testid="member-bo-doc-type">
                      <option value="">Select...</option>
                      {ID_DOC_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Last 4 of Doc # *</label>
                    <input type="text" maxLength={4} value={formData.bo_id_document_number_last4}
                      onChange={e => handleChange('bo_id_document_number_last4', e.target.value)}
                      className={inputSmCls} placeholder="1234" data-testid="member-bo-last4" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Issuing Country *</label>
                    <input type="text" maxLength={2} value={formData.bo_id_document_country}
                      onChange={e => handleChange('bo_id_document_country', e.target.value.toUpperCase())}
                      className={inputSmCls} placeholder="US" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onCancel} disabled={loading}
                className="px-4 py-2 text-sm text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                data-testid="add-member-cancel">
                Cancel
              </button>
              <button type="submit" disabled={loading}
                className="px-4 py-2 text-sm font-semibold bg-[#059669] text-black rounded-lg hover:bg-[#00D46E] disabled:opacity-50 flex items-center gap-2"
                data-testid="add-member-submit">
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</>
                ) : (
                  <><Plus className="w-4 h-4" /> {formData.is_beneficial_owner ? 'Add Member & Beneficial Owner' : 'Add Member'}</>
                )}
              </button>
            </div>
          </form>
    </div>
  );
};

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [boCount, setBoCount] = useState(null);
  const [stateAddress, setStateAddress] = useState({ ra_address: '', mf_address: '' });
  const [stateFiling, setStateFiling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [autoOpenBOAdd, setAutoOpenBOAdd] = useState(false);
  const [showAddOwner, setShowAddOwner] = useState(false);
  const [preselectBO, setPreselectBO] = useState(false);
  const [foreignRenewals, setForeignRenewals] = useState([]);
  const [toast, setToast] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [companyRes, membersRes, ordersRes, bosRes, stateAddrRes, stateFilingRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/companies/${companyId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { members: [] } })),
        axios.get(`${API}/orders?company_id=${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/companies/${companyId}/beneficial-owners`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { beneficial_owners: [] } })),
        axios.get(`${API}/companies/${companyId}/state-address`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { ra_address: '', mf_address: '' } })),
        // State filing — 404 means card hidden (inactive state for this entity type)
        axios.get(`${API}/companies/${companyId}/state-filing`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setCompany(companyRes.data);
      setMembers(membersRes.data.members || []);
      setOrders(ordersRes.data.orders || []);
      setBoCount((bosRes.data.beneficial_owners || []).length);
      setStateAddress({
        ra_address: stateAddrRes.data.ra_address || '',
        mf_address: stateAddrRes.data.mf_address || '',
      });
      setStateFiling(stateFilingRes.data);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load company details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId, token]);

  // Auto-load recurring compliance filings for international companies (any
  // monthly/quarterly/yearly product in the company's country shows here).
  useEffect(() => {
    if (!company || !company.country || company.country === 'US') { setForeignRenewals([]); return; }
    axios.get(`${API}/products`, { params: { country: company.country, limit: 100 }, headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const recurring = (res.data.products || []).filter(p => {
          const f = p.frequency_settings?.frequency;
          return p.pricing_logic && f && f !== 'one_time';
        });
        setForeignRenewals(recurring);
      })
      .catch(() => setForeignRenewals([]));
  }, [company, token]);


  // Scroll to #data-room (and other hash anchors) once the page has fully
  // hydrated. Browsers don't reliably do this when content mounts post-load.
  useEffect(() => {
    if (loading) return undefined;
    const hash = window.location.hash;
    if (!hash) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [loading]);

  const handleMemberAdded = () => {
    fetchData();
    setIsAddMemberOpen(false);
    setToast('Member added successfully!');
    setTimeout(() => setToast(null), 3000);
  };

  // Open the inline Add Member card and scroll the Members section into view.
  // Used by setup-banner, empty-state banner, and the Quick Actions sidebar
  // button so they always land on the same in-section card.
  const openAddMember = () => {
    setIsAddMemberOpen(true);
    // wait for the conditional render, then scroll
    setTimeout(() => {
      const el = document.getElementById('members-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  // Calculate expiry date (1 year from order date)
  const calculateExpiry = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      date.setFullYear(date.getFullYear() + 1);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  // Find RA and MF orders with tiered status logic
  // Active: approved, completed
  // Pending: pending, payment_pending, under_review, in_progress, documents_pending, payment_received
  // Not Added: no matching order
  
  const getServiceOrderStatus = (serviceType, productNamePattern) => {
    const matchingOrders = orders.filter(o => {
      const nameMatch = o.product_name?.toLowerCase().includes(productNamePattern);
      const serviceMatch = o.service_type === serviceType;
      return nameMatch || serviceMatch;
    });
    
    if (matchingOrders.length === 0) return { status: 'not_added', order: null };
    
    // Check for active orders first
    const activeOrder = matchingOrders.find(o => ['approved', 'completed'].includes(o.status));
    if (activeOrder) return { status: 'active', order: activeOrder };
    
    // Check for pending orders
    const pendingStatuses = ['pending', 'payment_pending', 'pending_review', 'under_review', 'in_progress', 'documents_pending', 'payment_received', 'draft'];
    const pendingOrder = matchingOrders.find(o => pendingStatuses.includes(o.status));
    if (pendingOrder) return { status: 'pending', order: pendingOrder };
    
    return { status: 'not_added', order: null };
  };
  
  const rawRaServiceStatus = getServiceOrderStatus('registered_agent', 'registered agent');
  const rawMfServiceStatus = getServiceOrderStatus('mail_forwarding', 'mail forwarding');

  // Manual-source override:
  // Backend enriches company.registered_agent_source/expiry from manual admin entries
  // when no live order exists. If admin manually set a date, render the Active card
  // even though there is no order in the orders collection (legacy / migrated clients).
  const applyManualOverride = (rawStatus, sourceVal, expiryVal) => {
    if (sourceVal === 'manual' && expiryVal) {
      return { status: 'active', order: null, manual: true, expiry: expiryVal };
    }
    return { ...rawStatus, manual: false };
  };

  const raServiceStatus = applyManualOverride(
    rawRaServiceStatus,
    company?.registered_agent_source,
    company?.registered_agent_expiry
  );
  const mfServiceStatus = applyManualOverride(
    rawMfServiceStatus,
    company?.mail_forwarding_source,
    company?.mail_forwarding_expiry
  );

  // Resolve expiry display: prefer backend-computed company.*_expiry,
  // else fall back to legacy +1yr from order.created_at.
  const resolveExpiryDisplay = (svcStatus, companyExpiry) => {
    if (companyExpiry) return formatLongDate(companyExpiry);
    return calculateExpiry(svcStatus.order?.created_at) || 'N/A';
  };

  // Legacy variables for backward compatibility
  const raOrder = raServiceStatus.status === 'active' ? raServiceStatus.order : null;
  const mfOrder = mfServiceStatus.status === 'active' ? mfServiceStatus.order : null;

  // Find EIN order
  const einOrder = orders.find(o => {
    const nameMatch = o.product_name?.toLowerCase().includes('ein') || o.product_name?.toLowerCase().includes('tax id');
    const serviceMatch = o.service_type === 'ein_application';
    return nameMatch || serviceMatch;
  });

  const einOrderCompleted = einOrder && ['approved', 'completed'].includes(einOrder.status);

  // Recent orders (last 5)
  const recentOrders = orders.slice(0, 5);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400">Loading company details...</div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error}</p>
          <Link to="/dashboard/companies" className="mt-2 text-sm text-red-400 hover:text-red-300 underline">
            Back to companies
          </Link>
        </div>
      </AppLayout>
    );
  }

  if (!company) {
    return (
      <AppLayout>
        <div className="text-center py-20">
          <p className="text-gray-400">Company not found</p>
          <Link to="/dashboard/companies" className="mt-2 text-sm text-[#059669] hover:underline">
            Back to companies
          </Link>
        </div>
      </AppLayout>
    );
  }

  const isInternational = !!(company.country && company.country !== 'US');
  const foreignOwners = Array.isArray(company.owners) ? company.owners : [];
  const usesOwnersArray = isInternational || foreignOwners.length > 0;
  const newOrderPath = isInternational
    ? `/dashboard/orders/international?company=${company.id}`
    : `/dashboard/orders/new?company=${company.id}`;
  const foreignBOs = foreignOwners.filter(o => {
    const roles = Array.isArray(o.roles) ? o.roles : (o.role ? [o.role] : []);
    return roles.some(r => (r || '').toLowerCase() === 'beneficial owner');
  });

  const openForeignOwner = (bo) => { setPreselectBO(!!bo); setShowAddOwner(true); };
  const onForeignOwnerAdded = () => { setShowAddOwner(false); fetchData(); };
  const downloadOwnerDoc = async (index) => {
    try {
      const res = await axios.get(`${API}/companies/${companyId}/owners/${index}/document`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) window.open(res.data.url, '_blank', 'noopener');
    } catch (e) {
      alert('Unable to open the document.');
    }
  };
  const FREQ_LABEL = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly', duration_based: 'Periodic' };


  return (
    <AppLayout>
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-[#059669] text-black font-medium rounded-lg shadow-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {toast}
        </div>
      )}

      {/* Back Link */}
      <Link 
        to="/dashboard/companies" 
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Companies
      </Link>

      {/* Company Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-[#111827] border border-gray-800 rounded-xl flex items-center justify-center">
            <Building2 className="w-8 h-8 text-[#059669]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{company.company_name}</h1>
            <p className="text-gray-400">{ENTITY_TYPES[company.entity_type] || company.entity_type}</p>
          </div>
        </div>
        
        <Link
          to={newOrderPath}
          className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: '#059669' }}
          data-testid="new-order-btn"
        >
          <Plus className="w-4 h-4" />
          New Order
        </Link>
      </div>

      {/* Complete Setup Banner — companies using the owners array */}
      {!loading && usesOwnersArray && foreignOwners.length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6" data-testid="intl-setup-banner">
          <div className="flex flex-col sm:flex-row items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-amber-400 font-medium mb-1">Complete your company setup</p>
              <p className="text-amber-300/80 text-sm mb-3">Add your members, directors and beneficial owners to continue.</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => openForeignOwner(false)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors text-sm" data-testid="intl-setup-add-member-btn">
                  <Users className="w-4 h-4" /> Add Member
                </button>
                <button onClick={() => openForeignOwner(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 font-medium rounded-lg hover:bg-amber-500/30 transition-colors text-sm" data-testid="intl-setup-add-bo-btn">
                  <UserCheck className="w-4 h-4" /> Add Beneficial Owner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Setup Banner — shown when members=0 OR BOs=0 */}
      {!loading && !usesOwnersArray && boCount !== null && (members.length === 0 || boCount === 0) && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6" data-testid="setup-banner">
          <div className="flex flex-col sm:flex-row items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-amber-400 font-medium mb-1">Complete your company setup</p>
              <p className="text-amber-300/80 text-sm mb-3">
                Add your members and beneficial owners to continue.
              </p>
              <div className="flex flex-wrap gap-2">
                {members.length === 0 && (
                  <button
                    onClick={openAddMember}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 transition-colors text-sm"
                    data-testid="setup-add-member-btn"
                  >
                    <Users className="w-4 h-4" />
                    Add Member
                  </button>
                )}
                {boCount === 0 && (
                  <button
                    onClick={() => setAutoOpenBOAdd(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 font-medium rounded-lg hover:bg-amber-500/30 transition-colors text-sm"
                    data-testid="setup-add-bo-btn"
                  >
                    <UserCheck className="w-4 h-4" />
                    Add Beneficial Owner
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Company Overview */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="company-overview">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[#059669]" />
              Company Overview
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">State of Formation</p>
                <p className="text-white flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {company.state_of_formation}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date of Formation</p>
                <p className="text-white flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {company.date_of_formation ? formatLongDate(company.date_of_formation) : 'Not specified'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Federal Tax ID (EIN)</p>
                {company.ein ? (
                  <p className="text-white">{company.ein}</p>
                ) : einOrderCompleted ? (
                  <p className="text-gray-400">Pending assignment</p>
                ) : (
                  <div>
                    <p className="text-gray-400">Not yet assigned</p>
                    {!einOrder && (
                      <p className="text-gray-500 text-xs mt-1">
                        EIN will be assigned once your EIN application order is completed.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">State Tax ID</p>
                <p className={company.state_tax_id ? 'text-white' : 'text-gray-400'} data-testid="company-state-tax-id">
                  {company.state_tax_id || 'Not yet assigned'}
                </p>
              </div>
              {(company.tax_identifiers || []).length > 0 && (
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Tax Identifiers (Sales Tax / VAT / GST)</p>
                  <div className="space-y-1" data-testid="company-tax-identifiers">
                    {(company.tax_identifiers || []).map((t, i) => (
                      <p key={i} className="text-white text-sm">
                        <span className="uppercase text-gray-500 text-xs mr-2">{(t.label || t.type || '').toString().replace('_', ' ')}</span>
                        {t.jurisdiction ? `${t.jurisdiction}: ` : ''}<span className="font-mono">{t.value}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
              {!isInternational && (
                <div>
                  <p className="text-sm text-gray-500">BOIR Filed</p>
                  <p className={company.boir_filed ? 'text-[#059669]' : 'text-gray-400'}>
                    {company.boir_filed ? 'Yes' : 'No'}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Registered Agent Section */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="registered-agent-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#059669]" />
                {raLabel(company.country)}
              </h2>
              {raServiceStatus.status === 'active' ? (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#059669] text-gray-900">
                  Active
                </span>
              ) : raServiceStatus.status === 'pending' ? (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                  Pending Activation
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#2D333B] text-gray-400">
                  Not Added
                </span>
              )}
            </div>
            
            {raServiceStatus.status === 'active' ? (
              <div className="space-y-3">
                {company.registered_agent_name && (
                  <div>
                    <p className="text-sm text-gray-500">Agent Name</p>
                    <p className="text-white">{company.registered_agent_name}</p>
                  </div>
                )}
                {company.registered_agent_address && (
                  <div>
                    <p className="text-sm text-gray-500">Agent Address</p>
                    <p className="text-white">
                      {[
                        company.registered_agent_address.street,
                        company.registered_agent_address.city,
                        company.registered_agent_address.state,
                        company.registered_agent_address.zip
                      ].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Expires</p>
                  <p className="text-white">{resolveExpiryDisplay(raServiceStatus, company.registered_agent_expiry)}</p>
                </div>
                <div data-testid="ra-state-address">
                  <p className="text-sm text-gray-500">Registered Agent Address</p>
                  <p className="text-white">
                    {stateAddress.ra_address && stateAddress.ra_address.trim()
                      ? stateAddress.ra_address
                      : '—'}
                  </p>
                </div>
              </div>
            ) : raServiceStatus.status === 'pending' ? (
              <div className="space-y-2">
                <p className="text-yellow-400 text-sm">Your {raLabel(company.country)} service is being processed.</p>
                <p className="text-gray-400 text-sm">Once activated, your agent details will appear here.</p>
              </div>
            ) : (
              <div>
                <p className="text-gray-400 text-sm mb-4">
                  A local legal representative or registered office is required to receive official government correspondence and legal notices.
                </p>
                <Link
                  to={isInternational ? `/dashboard/orders/international?company=${company.id}` : `/dashboard/orders/new?service=registered_agent&company=${company.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
                  style={{ backgroundColor: '#059669' }}
                  data-testid="add-ra-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Registered Agent
                </Link>
              </div>
            )}
          </section>

          {/* Mail Forwarding Section */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="mail-forwarding-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#059669]" />
                Mail Forwarding
              </h2>
              {mfServiceStatus.status === 'active' ? (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#059669] text-gray-900">
                  Active
                </span>
              ) : mfServiceStatus.status === 'pending' ? (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                  Pending Activation
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#2D333B] text-gray-400">
                  Not Added
                </span>
              )}
            </div>
            
            {mfServiceStatus.status === 'active' ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Service Status</p>
                  <p className="text-[#059669]">Active</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expires</p>
                  <p className="text-white">{resolveExpiryDisplay(mfServiceStatus, company.mail_forwarding_expiry)}</p>
                </div>
                <div data-testid="mf-state-address">
                  <p className="text-sm text-gray-500">Mail Forwarding Address</p>
                  <p className="text-white">
                    {stateAddress.mf_address && stateAddress.mf_address.trim()
                      ? stateAddress.mf_address
                      : '—'}
                  </p>
                </div>
              </div>
            ) : mfServiceStatus.status === 'pending' ? (
              <div className="space-y-2">
                <p className="text-yellow-400 text-sm">Your Mail Forwarding service is being processed.</p>
                <p className="text-gray-400 text-sm">Once activated, your mailing address will appear here.</p>
              </div>
            ) : (
              <div>
                <p className="text-gray-400 text-sm mb-4">
                  Get a professional corporate address with secure digital mail scanning and global forwarding services.
                </p>
                <Link
                  to={isInternational ? `/dashboard/orders/international?company=${company.id}` : `/dashboard/orders/new?service=mail_forwarding&company=${company.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
                  style={{ backgroundColor: '#059669' }}
                  data-testid="add-mf-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Mail Forwarding
                </Link>
              </div>
            )}
          </section>

          {/* State Filing Card — hidden when inactive for state×entity */}
          {stateFiling && stateFiling.is_active && (
            <section
              className="bg-[#111827] border border-gray-800 rounded-lg p-6"
              data-testid="state-filing-section"
            >
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-5 h-5 text-[#059669]" />
                  <div>
                    <h2 className="text-lg font-semibold text-white">Annual State Filing</h2>
                    <p className="text-sm text-gray-400 mt-0.5">
                      {stateFiling.state_name} {stateFiling.filing_name}
                    </p>
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    {
                      green:  'bg-[#059669]/20 text-[#059669]',
                      red:    'bg-red-500/20 text-red-400',
                      orange: 'bg-orange-500/20 text-orange-400',
                      amber:  'bg-amber-500/20 text-amber-400',
                      grey:   'bg-gray-600/30 text-gray-300',
                    }[stateFiling.status?.tone] || 'bg-gray-600/30 text-gray-300'
                  }`}
                  data-testid="state-filing-status-badge"
                >
                  {stateFiling.status?.label}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Due Date</p>
                  <p className="text-white" data-testid="state-filing-due-date">
                    {stateFiling.due_date
                      ? formatLongDate(stateFiling.due_date)
                      : 'Set your formation date'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Frequency</p>
                  <p className="text-white capitalize">
                    {stateFiling.frequency_years > 1
                      ? `Every ${stateFiling.frequency_years} years`
                      : 'Annual'}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-800 pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Government Fee</span>
                  {stateFiling.fee_type === 'variable' ? (
                    <span className="text-gray-300 text-right max-w-[60%]" data-testid="state-filing-govt-fee">
                      {stateFiling.govt_fee_note || 'Calculated before filing'}
                    </span>
                  ) : (
                    <span className="text-white font-mono" data-testid="state-filing-govt-fee">
                      ${Number(stateFiling.govt_fee).toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Our Fee</span>
                  <span className="text-white font-mono">
                    ${Number(stateFiling.platform_fee).toFixed(2)}
                  </span>
                </div>
                {stateFiling.fee_type === 'fixed' && (
                  <div className="flex justify-between pt-2 border-t border-gray-800">
                    <span className="text-gray-300 font-medium">Total</span>
                    <span className="text-white font-mono font-semibold" data-testid="state-filing-total">
                      ${Number(stateFiling.total_amount).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Pay Now CTA — appears only when admin has approved the auto-created filing order */}
              {stateFiling.has_active_order && stateFiling.active_order_status === 'payment_pending' && (
                <div className="mt-4">
                  <Link
                    to={`/dashboard/orders/${stateFiling.active_order_id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-[#059669] text-[#0F141B] hover:bg-[#00d171] transition-colors"
                    data-testid="state-filing-pay-now-btn"
                  >
                    Pay Now
                  </Link>
                </div>
              )}
              {/* Generic active-order note (other in-progress statuses) */}
              {stateFiling.has_active_order && stateFiling.active_order_status !== 'payment_pending' && (
                <div className="mt-4 p-3 bg-gray-900/50 border border-gray-800 rounded text-xs text-gray-400" data-testid="state-filing-active-order-note">
                  A filing order is already in progress (status: {stateFiling.active_order_status}).
                </div>
              )}
            </section>
          )}

          {/* International Members / Owners (from onboarding) */}
          {usesOwnersArray && (
            <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="intl-members-section">
              <h2 className="text-lg font-semibold text-white flex items-center justify-between gap-2 mb-4">
                <span className="flex items-center gap-2"><Users className="w-5 h-5 text-[#059669]" /> Members / Directors / Officers</span>
                <button onClick={() => openForeignOwner(false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#059669' }} data-testid="intl-add-member-btn">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </h2>
              {foreignOwners.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Name</th>
                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Type</th>
                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Role</th>
                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Ownership %</th>
                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Nationality / Country</th>
                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Document</th>
                      </tr>
                    </thead>
                    <tbody>
                      {foreignOwners.map((o, i) => (
                        <tr key={i} className="border-b border-gray-800/50" data-testid={`intl-owner-row-${i}`}>
                          <td className="py-3 text-white align-top">{o.full_name}</td>
                          <td className="py-3 text-gray-400 align-top">{o.owner_type === 'company' ? 'Company' : 'Individual'}</td>
                          <td className="py-3 text-gray-400 align-top">{(Array.isArray(o.roles) && o.roles.length ? o.roles.join(', ') : (o.role || '-'))}</td>
                          <td className="py-3 text-gray-400 align-top">{o.ownership_percentage != null ? `${o.ownership_percentage}%` : '-'}</td>
                          <td className="py-3 text-gray-400 align-top">{o.nationality || '-'}</td>
                          <td className="py-3 align-top">
                            {o.document_url || o.document_s3_key ? (
                              <button onClick={() => downloadOwnerDoc(i)} className="text-[#059669] hover:underline text-sm" data-testid={`intl-owner-doc-${i}`}>
                                {o.document_type === 'formation_certificate' ? 'Certificate' : 'Passport'}
                              </button>
                            ) : <span className="text-gray-500 text-sm">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No owners recorded.</p>
              )}
            </section>
          )}

          {/* International Beneficial Owners */}
          {usesOwnersArray && (
            <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="intl-bo-section">
              <h2 className="text-lg font-semibold text-white flex items-center justify-between gap-2 mb-4">
                <span className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#059669]" /> Beneficial Owners</span>
                <button onClick={() => openForeignOwner(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#059669' }} data-testid="intl-add-bo-btn">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </h2>
              {foreignBOs.length > 0 ? (
                <div className="space-y-3">
                  {foreignBOs.map((o, i) => (
                    <div key={i} className="flex items-center justify-between border border-gray-800 rounded-lg px-4 py-3" data-testid={`intl-bo-row-${i}`}>
                      <div>
                        <p className="text-white text-sm font-medium">{o.full_name}</p>
                        <p className="text-gray-500 text-xs">{o.owner_type === 'company' ? 'Company' : 'Individual'} · {o.nationality || '-'}</p>
                      </div>
                      <span className="text-[#059669] text-sm font-semibold">{o.ownership_percentage != null ? `${o.ownership_percentage}%` : '-'}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No owner has been marked as a Beneficial Owner.</p>
              )}
            </section>
          )}

          {/* Compliance & Renewals — auto-listed recurring filings for the country */}
          {isInternational && foreignRenewals.length > 0 && (
            <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="intl-renewals-section">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-1">
                <CalendarDays className="w-5 h-5 text-[#059669]" /> Compliance &amp; Renewals
              </h2>
              <p className="text-gray-400 text-sm mb-4">Recurring filings this company must keep current. File on time to avoid penalties.</p>
              <div className="space-y-3">
                {foreignRenewals.map((p) => (
                  <div key={p.id} className="border border-gray-800 rounded-lg p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3" data-testid={`renewal-${p.service_type}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white text-sm font-medium">{p.product_name}</p>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-[#059669]/15 text-[#059669]">{FREQ_LABEL[p.frequency_settings?.frequency] || 'Recurring'}</span>
                      </div>
                      {p.filing_notes && <p className="text-gray-400 text-xs mt-1 leading-relaxed">{p.filing_notes}</p>}
                      <p className="text-gray-300 text-sm mt-2">
                        {`$${(p.platform_fees || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} platform{p.has_state_fee ? ' + government fee' : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/dashboard/orders/international?company=${companyId}&product=${p.service_type}`)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white whitespace-nowrap self-start"
                      style={{ backgroundColor: '#059669' }}
                      data-testid={`renewal-order-${p.service_type}`}
                    >
                      <ShoppingCart className="w-4 h-4" /> Create Order
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}


          {/* Members Section */}
          {!usesOwnersArray && (
          <section id="members-section" className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="members-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#059669]" />
                Members / Directors / Officers
              </h2>
              {!isAddMemberOpen && members.length > 0 && (
                <button 
                  onClick={() => setIsAddMemberOpen(true)}
                  className="text-sm text-[#059669] px-3 py-1 rounded border border-[#059669] hover:bg-[#059669]/10 flex items-center gap-1 transition-colors"
                  data-testid="add-member-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Member
                </button>
              )}
            </div>

            {/* Inline Add Member card (replaces former drawer) */}
            {isAddMemberOpen && (
              <AddMemberCard
                onCancel={() => setIsAddMemberOpen(false)}
                onSuccess={handleMemberAdded}
                companyId={companyId}
                entityType={company.entity_type}
                token={token}
              />
            )}
            
            {members.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left text-sm font-medium text-gray-500 pb-3">Name</th>
                      <th className="text-left text-sm font-medium text-gray-500 pb-3">Title</th>
                      <th className="text-left text-sm font-medium text-gray-500 pb-3">Ownership %</th>
                      <th className="text-left text-sm font-medium text-gray-500 pb-3">US Resident</th>
                      <th className="text-left text-sm font-medium text-gray-500 pb-3">Passport</th>
                      <th className="text-left text-sm font-medium text-gray-500 pb-3">KYC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member, index) => (
                      <tr key={member.id || index} className="border-b border-gray-800/50" data-testid={`member-row-${member.id}`}>
                        <td className="py-3 text-white align-top">{member.full_legal_name}</td>
                        <td className="py-3 text-gray-400 align-top">
                          <div className="flex flex-col">
                            <span>{member.us_title || '-'}</span>
                            {member.title_updated_by_admin_at && (
                              <span
                                className="text-[10px] text-blue-300/70 mt-0.5 whitespace-nowrap"
                                data-testid={`member-title-admin-audit-${member.id}`}
                                title={`Updated by Admin on ${formatLongDate(member.title_updated_by_admin_at)}`}
                              >
                                Admin · {formatLongDate(member.title_updated_by_admin_at)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-gray-400 align-top">
                          {member.ownership_percentage ? `${member.ownership_percentage}%` : '-'}
                        </td>
                        <td className="py-3 align-top">
                          {member.is_us_resident ? (
                            <span className="text-[#059669]">Yes</span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                        <td className="py-3 align-top">
                          <MemberPassportCell
                            companyId={companyId}
                            memberId={member.id}
                            token={token}
                          />
                        </td>
                        <td className="py-3 align-top">
                          <ClientKYCBadge member={member} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4" data-testid="member-reminder-banner">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div className="flex-1">
                    <p className="text-amber-400 font-medium mb-1">Action Required</p>
                    <p className="text-amber-300/80 text-sm mb-3">
                      Please add at least one member or director to your company. This is required to complete your formation.
                    </p>
                    <button 
                      onClick={openAddMember}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-black font-medium rounded hover:bg-amber-400 transition-colors text-sm"
                      data-testid="add-member-banner-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Member
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>
          )}

          {/* Beneficial Owners Section */}
          {!usesOwnersArray && (
          <BeneficialOwnersSection
            companyId={companyId}
            company={company}
            token={token}
            onDataChange={() => { fetchData(); setAutoOpenBOAdd(false); }}
            autoOpenAdd={autoOpenBOAdd}
          />
          )}

          {/* Data Room Section (Stage 8 — Secure VDR) */}
          <ClientDataRoomSection companyId={companyId} token={token} />
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Recent Orders */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6" data-testid="recent-orders-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#059669]" />
                Recent Orders
              </h2>
            </div>
            
            {recentOrders.length > 0 ? (
              <div className="space-y-3">
                {recentOrders.map((order) => {
                  const statusStyle = STATUS_STYLES[order.status] || STATUS_STYLES.draft;
                  return (
                    <div 
                      key={order.id} 
                      className="flex items-center justify-between p-3 bg-[#0b1310] rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{order.product_name}</p>
                        <p className="text-gray-500 text-xs">{formatDate(order.created_at)}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </div>
                  );
                })}
                
                <Link 
                  to="/dashboard/orders" 
                  className="flex items-center justify-center gap-1 text-sm text-[#059669] hover:underline mt-4"
                >
                  View All Orders
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No orders yet</p>
            )}
          </section>

          {/* Quick Actions */}
          <section className="bg-[#111827] border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <Link
                to={newOrderPath}
                className="block w-full px-4 py-3 bg-[#0b1310] border border-gray-800 rounded-lg text-white text-sm hover:border-[#059669] transition-colors"
              >
                + New Order
              </Link>
              <button
                onClick={openAddMember}
                className="block w-full px-4 py-3 bg-[#0b1310] border border-gray-800 rounded-lg text-white text-sm hover:border-[#059669] transition-colors text-left"
              >
                + Add Member
              </button>
            </div>
          </section>
        </div>
      </div>
      {showAddOwner && (
        <AddForeignOwnerModal
          companyId={companyId}
          token={token}
          api={API}
          preselectBO={preselectBO}
          onClose={() => setShowAddOwner(false)}
          onAdded={onForeignOwnerAdded}
        />
      )}

    </AppLayout>
  );
};

export default CompanyDetailPage;
